import React, { useMemo } from 'react'

import { Heading, Paragraph } from '../../components'
import { useFinancialsTableContext } from '../../components/financials-table'
import { EarningsEstimateType } from '../earnings-common/types'
import { ChartMonths } from '../forecast/charts/ChartMonths'
import { EstimateTypeChipSwitch } from './EstimateTypeChipSwitch'
import { FiscalPeriodSelector } from './FiscalPeriodSelector'
import { PriceRevisionsChart } from './charts/PriceRevisionsChart'
import { UpDownRevisionsChart } from './charts/UpDownRevisionsChart'
import { FISCAL_YEAR_MARK, PRICE_REVISIONS_CHART_WIDTH } from './constants'
import { EarningsRevisionsByType, EarningsSettings, RevisionsChartData } from './types'
import { adjustEstimate } from './utils'

function getDefaultFiscalPeriod(revisionData: EarningsRevisionsByType, estimateType: EarningsEstimateType) {
  const years = Object.keys(revisionData[estimateType].years)
  return years[years.length - 1]
}

interface Props {
  revisionData: EarningsRevisionsByType
}

export default function EstimateRevisions({ revisionData }: Props) {
  const [priceRevisionIsActive, setPriceRevisionIsActive] = React.useState(false)
  const [upDownRevisionIsActive, setUpDownRevisionIsActive] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState(-1)
  const { settings, setSettings } = useFinancialsTableContext<EarningsSettings>()

  const estimateType = settings.estimateType
  const prepFiscalPeriod = settings.fiscalPeriod
    ? settings.fiscalPeriod
    : getDefaultFiscalPeriod(revisionData, estimateType)

  const data = useMemo(() => {
    const chartData: RevisionsChartData = []
    const years = Object.keys(revisionData[estimateType].years)
    const quarters = Object.keys(revisionData[estimateType].quarters)
    const fiscalPeriods = [...years, ...quarters]
    let fiscalPeriod = prepFiscalPeriod
    if (!fiscalPeriods.includes(fiscalPeriod)) {
      fiscalPeriod = prepFiscalPeriod.endsWith(FISCAL_YEAR_MARK)
        ? years[years.length - 1]
        : quarters[quarters.length - 1]
    }

    const revisions = fiscalPeriod.endsWith(FISCAL_YEAR_MARK)
      ? revisionData[estimateType].years[fiscalPeriod]
      : revisionData[estimateType].quarters[fiscalPeriod]
    for (const revision of revisions) {
      chartData.push({
        ...revision,
        high: adjustEstimate(revision.high, estimateType),
        low: adjustEstimate(revision.low, estimateType),
        lowHigh: [adjustEstimate(revision.low, estimateType), adjustEstimate(revision.high, estimateType)],
        mean: adjustEstimate(revision.mean, estimateType),
        negDownRevisions: -revision.downRevisions,
        timestamp: revision.date.getTime(),
      })
    }
    return {
      chartData,
      fiscalPeriod,
      fiscalPeriods,
    }
  }, [estimateType, prepFiscalPeriod, revisionData])

  const isActive = priceRevisionIsActive || upDownRevisionIsActive

  return (
    <div className="w-full">
      <div className="ml-auto mr-auto flex items-center pb-3" style={{ width: PRICE_REVISIONS_CHART_WIDTH }}>
        <Heading level={4} className="pl-1.5 text-muted">
          Estimate Revisions
        </Heading>
        <div className="flex-grow"></div>
        <EstimateTypeChipSwitch label="Estimate" />
        <Paragraph size="small" className="ml-5 pr-1 text-muted-2 font-medium">
          Period
        </Paragraph>
        <div className="ml-2 inline-block w-22">
          <FiscalPeriodSelector
            selected={data.fiscalPeriod}
            fiscalPeriods={data.fiscalPeriods}
            onSelect={(selected) => setSettings({ ...settings, fiscalPeriod: selected })}
          />
        </div>
      </div>
      <PriceRevisionsChart
        activeIndex={activeIndex}
        chartData={data.chartData}
        estimateType={estimateType}
        isActive={isActive}
        onSetActiveIndex={setActiveIndex}
        onSetIsActive={setPriceRevisionIsActive}
      />
      <ChartMonths chartData={data.chartData} />
      <UpDownRevisionsChart
        activeIndex={activeIndex}
        chartData={data.chartData}
        isActive={isActive}
        onSetActiveIndex={setActiveIndex}
        onSetIsActive={setUpDownRevisionIsActive}
      />
    </div>
  )
}
